import React, { useRef } from 'react';
import { observer } from 'mobx-react-lite';
import { toJS } from 'mobx';
import { Button, colors, Modal, Select } from '@insurancemenu/imenu-components-lib';
import AgentTag from 'components/common/group/sidebar/AgentType';
import FormsSpinner from 'components/common/loaders/FormSpinner';
import { AssigneeStatuses } from 'store/ui-stores/group-info/assignAgentsModalStore';
import { useRootStore } from 'store/rootStore';
import { celticsRights } from '@insurancemenu/imenu-components-lib/dist/utils/accessRights';
import { createStyles } from './styles';

interface Props {
  show: boolean;
  onClose: () => void;
  isClosable: boolean;
}

const AssignAgentsModal: React.FC<Props> = observer(({ show, onClose, isClosable }) => {
  const { assignAgentsModalStore, userStore, groupSidebarStore, groupInfoController } = useRootStore();
  const scrollRef = useRef<HTMLDivElement>(null);
  const accountReps = assignAgentsModalStore.accountReps.map((dateObj) => toJS(dateObj)).slice();
  const groupId = groupSidebarStore.group.id;

  async function onSave(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    scrollRef.current?.scrollTo(0, 0);
    await assignAgentsModalStore.updateAssigneesAndAgentsOfRecord();
    onClose();
    void groupInfoController.requestInitialData(groupId);
  }

  function onCloseWithScroll(): void {
    scrollRef.current?.scrollTo(0, 0);
    onClose();
  }

  function onAccountRepChange(e: React.ChangeEvent<HTMLSelectElement>): void {
    assignAgentsModalStore.setSelectedAccountRepId(e.target.value);
  }

  const classes = createStyles();
  return (
    <Modal
      zIndex={1001}
      show={show}
      onClose={onCloseWithScroll}
      closable={isClosable}
      width="65.6rem"
      className={classes.modal}
      modalContainerRef={scrollRef}
      id="assign-agents"
    >
      <h3 className={classes.title} id="title">
        Assign Agents
      </h3>
      <h4 className={classes.subTitle}>{assignAgentsModalStore.rootStore.groupStore.group.name}</h4>
      <form className={classes.form} id="agents-form" onSubmit={onSave}>
        {accountReps.length > 0 && (
          <>
            <h5 className={classes.sectionTitle}>Account Representative:</h5>
            <Select
              name="accountRepresentative"
              options={accountReps}
              value={assignAgentsModalStore.selectedAccountRep}
              onChange={onAccountRepChange}
              className={classes.modalSelect}
              disabled={!celticsRights(userStore.userRoles)}
            />
          </>
        )}
        <div className={classes.agentsGroupWrapper}>
          <h5 className={classes.sectionTitle}>Assigned:</h5>
          <ul className={classes.agentsList} id="assigned-agents-list">
            {assignAgentsModalStore.assignedAssignees.map((agent) => (
              <li id={agent.id} className={classes.agentRecord} key={`agent-unassigned_name:${agent.id}-${agent.name}`}>
                <span className={classes.agent}>
                  <span className={classes.agentName}>{agent.name}</span>
                  <AgentTag type={agent.type} />
                </span>
                <Button
                  id="unassign-agent"
                  type="text"
                  color="custom"
                  customColor={colors.red[500]}
                  onClick={(): void => {
                    assignAgentsModalStore.changeAssigneeStatus(agent.id, AssigneeStatuses.UNASSIGNED);
                  }}
                  className={classes.agentActionButton}
                >
                  Unassign
                </Button>
              </li>
            ))}
          </ul>
        </div>
        <div className={classes.agentsGroupWrapper}>
          <h5 className={classes.sectionTitle}>Available:</h5>
          <ul className={classes.agentsList} id="available-agents-list">
            {assignAgentsModalStore.unassignedAssignees.map((agent) => (
              <li id={agent.id} className={classes.agentRecord} key={`agent-assigned_name:${agent.id}-${agent.name}`}>
                <span className={classes.agent}>
                  <span className={classes.agentName}>{agent.name}</span>
                  <AgentTag type={agent.type} />
                </span>
                <Button
                  id="assign-agent"
                  type="text"
                  color="primary"
                  onClick={(): void => {
                    assignAgentsModalStore.changeAssigneeStatus(agent.id, AssigneeStatuses.ASSIGNED);
                  }}
                  className={classes.agentActionButton}
                >
                  Assign
                </Button>
              </li>
            ))}
          </ul>
        </div>
      </form>
      <footer className={classes.modalFooter}>
        {isClosable && (
          <Button
            onClick={onCloseWithScroll}
            type="outlined"
            color="primary"
            size="medium"
            width="16rem"
            id="close-modal-button"
          >
            Cancel
          </Button>
        )}
        <Button
          color="primary"
          size="medium"
          width="16rem"
          htmlType="submit"
          htmlForm="agents-form"
          id="save-agents-button"
        >
          Save
        </Button>
      </footer>
      <FormsSpinner display={assignAgentsModalStore.loading} />
    </Modal>
  );
});

export default AssignAgentsModal;
